import {AdditiveBlending, DoubleSide, Mesh, RingGeometry, ShaderMaterial, type Color, type Object3D} from 'three'

import {RING_INNER_RATIO, RING_LIFT_M, RING_PEAK_RATIO, RING_SEGMENTS} from '../config'
import {easeInCubic, easeOutCubic} from '../util/easing'

/**
 * The expanding ring of light under the model's feet.
 *
 * `RingGeometry` + a shader rather than a quad with a radial-gradient texture,
 * for three reasons that all point the same way:
 *
 *  1. Fill rate is the only cost that matters here. The visible part of a ring
 *     is the annulus, pi*(R^2 - (0.55R)^2) ~= 2.2*R^2. A quad rasterises 4*R^2
 *     and throws away the 21% outside the circle - roughly 3x the fragments for
 *     the same picture, on the one platform where additive fill is expensive.
 *  2. A 1.2 m ring seen from MIN_PLACE_DISTANCE_M fills the screen. A 128 px
 *     texture stretched that far shows 8-bit banding; an analytic profile does
 *     not care about distance.
 *  3. Zero bytes. The whole point of this effect is that it adds nothing to the
 *     ~1.9 MB first load.
 *
 * `Sprite` was never an option - it is always camera-facing, and this ring has
 * to lie flat on the floor.
 *
 * Not chosen but worth recording: RingGeometry's uv is a bounding-square
 * mapping, so an ordinary square texture DOES map correctly onto the annulus.
 * If a campaign ever needs a pattern that cannot be written as a formula (runes,
 * a logo), swap this ShaderMaterial for a MeshBasicMaterial with a map and
 * nothing else here changes.
 */

export interface ShockwaveRing {
  /** Parent this wherever the effect lives. */
  readonly object: Object3D
  /**
   * `0` is the instant of impact, `1` is gone. Values outside that range hide
   * the ring, so the caller never has to special-case "not running".
   */
  setProgress(progress: number): void
  dispose(): void
}

export interface ShockwaveRingOptions {
  /** Outer radius in metres at progress 1. */
  readonly maxRadiusMeters: number
  /** Shared with the sparks. Already in the renderer's working colour space. */
  readonly color: Color
}

const vertexShader = /* glsl */ `
varying float vRadius;

void main() {
  // RingGeometry lies in the XY plane, so the local radius is already in the
  // position attribute - no extra attribute and no uv dependency.
  vRadius = length(position.xy);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

const fragmentShader = /* glsl */ `
uniform vec3 uColor;
uniform float uOpacity;
varying float vRadius;

void main() {
  // Both ends of the profile reach zero INSIDE the geometry, so the 48-segment
  // outer polygon is never a visible hard edge and needs no antialiasing.
  float body = smoothstep(${RING_INNER_RATIO.toFixed(3)}, ${RING_PEAK_RATIO.toFixed(3)}, vRadius);
  float head = 1.0 - smoothstep(${RING_PEAK_RATIO.toFixed(3)}, 1.0, vRadius);
  // AdditiveBlending is (SRC_ALPHA, ONE), so rgb gets scaled by alpha for us.
  gl_FragColor = vec4(uColor, body * head * uOpacity);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`

export const createShockwaveRing = ({
  maxRadiusMeters,
  color,
}: ShockwaveRingOptions): ShockwaveRing => {
  // Unit outer radius, built once. The radius animates through `mesh.scale`,
  // never by rebuilding geometry or rewriting attributes - and scaling from ~0
  // means the brightest frame is also the cheapest one to fill.
  const geometry = new RingGeometry(RING_INNER_RATIO, 1, RING_SEGMENTS, 1)
  // The shader reads neither. Dropping them removes two vertex streams from the
  // draw; unused attributes are optimised out of the program either way.
  geometry.deleteAttribute('normal')
  geometry.deleteAttribute('uv')

  const uniforms = {uColor: {value: color}, uOpacity: {value: 0}}

  const material = new ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    blending: AdditiveBlending,
    // Writes no depth of its own, but still tests: the model's feet must occlude
    // the far side of the ring.
    depthWrite: false,
    // Costs no extra covered pixels (back-face culling is draw-or-do-not, not
    // more fragments) and keeps the ring visible when a table-top placement is
    // viewed from slightly below.
    side: DoubleSide,
  })

  const mesh = new Mesh(geometry, material)
  mesh.name = 'entrance-ring'
  mesh.rotation.x = -Math.PI / 2
  // ShadowMaterial does not disable depth writes, so the shadow catcher at
  // y = 0.001 writes depth and this has to clear it on its own.
  mesh.position.y = RING_LIFT_M
  // Near-coplanar transparent surfaces sort unstably by distance alone, which
  // shows up as the shadow intermittently dimming the light. Ordering says what
  // the picture means: shadow 0, lead-in prints 1, this 2, sparks 3 - matter on
  // top of shadow, light on top of matter.
  mesh.renderOrder = 2
  mesh.visible = false

  return {
    object: mesh,
    setProgress: (progress) => {
      if (progress < 0 || progress >= 1) {
        mesh.visible = false
        return
      }
      mesh.visible = true
      mesh.scale.setScalar(maxRadiusMeters * easeOutCubic(progress))
      // Holds near full brightness and then falls off a cliff. Fading linearly
      // while the area grows reads as a slowly dying blob, not an impact.
      uniforms.uOpacity.value = 1 - easeInCubic(progress)
    },
    dispose: () => {
      mesh.removeFromParent()
      geometry.dispose()
      material.dispose()
    },
  }
}
